import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { User, GraduationCap, BookOpen, Users, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SearchResult {
  id: string;
  name: string;
  type: "student" | "instructor" | "subject" | "batch";
  subtitle?: string;
}

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Keyboard shortcut handler
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const search = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    const searchTerm = `%${searchQuery}%`;

    try {
      const [studentsRes, instructorsRes, subjectsRes, batchesRes] = await Promise.all([
        supabase
          .from("students")
          .select("id, first_name, last_name, office_name")
          .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm},office_name.ilike.${searchTerm}`)
          .limit(5),
        supabase
          .from("instructors")
          .select("id, name, email")
          .or(`name.ilike.${searchTerm},email.ilike.${searchTerm}`)
          .limit(5),
        supabase
          .from("subjects")
          .select("id, name")
          .ilike("name", searchTerm)
          .limit(5),
        supabase
          .from("batches")
          .select("id, name")
          .ilike("name", searchTerm)
          .limit(5),
      ]);

      const allResults: SearchResult[] = [];

      if (studentsRes.data) {
        studentsRes.data.forEach((s) => {
          allResults.push({
            id: s.id,
            name: `${s.first_name} ${s.last_name || ""}`.trim(),
            type: "student",
            subtitle: s.office_name,
          });
        });
      }

      if (instructorsRes.data) {
        instructorsRes.data.forEach((i) => {
          allResults.push({
            id: i.id,
            name: i.name,
            type: "instructor",
            subtitle: i.email,
          });
        });
      }

      if (subjectsRes.data) {
        subjectsRes.data.forEach((s) => {
          allResults.push({
            id: s.id,
            name: s.name,
            type: "subject",
          });
        });
      }

      if (batchesRes.data) {
        batchesRes.data.forEach((b) => {
          allResults.push({
            id: b.id,
            name: b.name,
            type: "batch",
          });
        });
      }

      setResults(allResults);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      search(query);
    }, 300);

    return () => clearTimeout(debounce);
  }, [query, search]);

  const handleSelect = (result: SearchResult) => {
    setOpen(false);
    setQuery("");
    
    switch (result.type) {
      case "student":
        navigate(`/students/${result.id}`);
        break;
      case "instructor":
        navigate(`/attendance/instructor`);
        break;
      case "subject":
        navigate(`/subjects/add`);
        break;
      case "batch":
        navigate(`/batches/add`);
        break;
    }
  };

  const getIcon = (type: SearchResult["type"]) => {
    switch (type) {
      case "student":
        return <GraduationCap className="h-4 w-4" />;
      case "instructor":
        return <User className="h-4 w-4" />;
      case "subject":
        return <BookOpen className="h-4 w-4" />;
      case "batch":
        return <Users className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: SearchResult["type"]) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="relative h-9 w-9 sm:w-64 sm:justify-start sm:px-3 sm:py-2"
        onClick={() => setOpen(true)}
      >
        <Search className="h-4 w-4 sm:mr-2" />
        <span className="hidden sm:inline-flex">Search...</span>
        <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-6 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search students, instructors, subjects..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>
            {isLoading ? "Searching..." : "No results found."}
          </CommandEmpty>
          
          {results.length > 0 && (
            <>
              {["student", "instructor", "subject", "batch"].map((type) => {
                const typeResults = results.filter((r) => r.type === type);
                if (typeResults.length === 0) return null;

                return (
                  <CommandGroup key={type} heading={`${getTypeLabel(type as SearchResult["type"])}s`}>
                    {typeResults.map((result) => (
                      <CommandItem
                        key={`${result.type}-${result.id}`}
                        value={`${result.type}-${result.name}`}
                        onSelect={() => handleSelect(result)}
                      >
                        <div className="mr-2 text-muted-foreground">
                          {getIcon(result.type)}
                        </div>
                        <div className="flex flex-col">
                          <span>{result.name}</span>
                          {result.subtitle && (
                            <span className="text-xs text-muted-foreground">
                              {result.subtitle}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};
